/**
 * VCM Constants — sourced from demo.html and DCM Fields & Formats spreadsheet
 */

/** Case status values — from demo.html case profile tab (line 724) */
export const CASE_STATUSES = [
  'Pending',
  'Open',
  'Review',
  'On Wait List',
  'Closed',
  'Duplicate',
] as const;
export type CaseStatus = (typeof CASE_STATUSES)[number];

/** Case progression values — from demo.html case profile tab (line 724) */
export const CASE_PROGRESSIONS = [
  'Intake',
  'Assessing',
  'Planning',
  'Advocacy',
  'Monitoring',
  'Finishing',
] as const;
export type CaseProgression = (typeof CASE_PROGRESSIONS)[number];

/** Tier / priority levels */
export const CASE_TIERS = ['Tier 1', 'Tier 2', 'Tier 3', 'Tier 4'] as const;
export type CaseTier = (typeof CASE_TIERS)[number];

/** Tier definitions — veterans service priority levels */
export const TIER_DEFINITIONS: Record<CaseTier, string> = {
  'Tier 1':
    'Stable veteran with minor unmet needs; monthly monitoring to update status or the case may be closed due to lack of a resource for the identified need.',
  'Tier 2':
    'Some unmet service needs or veteran is actively navigating a benefits claim; monthly contact to monitor progress.',
  'Tier 3':
    'Significant unmet needs (e.g., homeless, complex VA claim, mental health crisis); a service plan is being developed and monitored, with bi-weekly or weekly contact.',
  'Tier 4':
    'Immediate and complex unmet needs; the veteran may have limited capacity due to disability, trauma, age, or income. Weekly contact is required.',
};

/** Case status definitions */
export const STATUS_DEFINITIONS: Record<string, string> = {
  Pending:
    'Use for case activity before the case is opened for active case management, including outreach, eligibility review, screening, intake, and other preliminary activities.',
  Open: 'Use for cases in active case management. Keep the case Open while services are being provided, follow-up is needed, or closure has not been fully completed.',
  Review:
    'Use when additional review is required before further action, such as supervisory review, eligibility clarification, documentation verification, unmet-needs reassessment, or review before closure.',
  'On Wait List':
    'Use when a client may receive services but case activity cannot proceed due to program capacity, timing, or other operational limitations.',
  Closed: 'Use only after case closure information has been fully completed and documented.',
  Duplicate:
    "Use when more than one case record exists for the same client. Note the Case ID where the client's information and services will be managed.",
};

/** Closure guidance text */
export const CLOSURE_GUIDANCE =
  'If closure documentation is not complete, keep the Case Status set to Open and set Case Progression to Finishing.';

/** Client connect status options */
export const CLIENT_CONNECT_STATUSES = [
  'All Needs Met',
  'Requesting Follow Up',
  'Duplicate',
  'Ready for Discharge',
] as const;

/** Contact initiated by options */
export const CONTACT_INITIATED_BY = ['Client', 'Case manager', 'Partner organization'] as const;

/** Level of client contact options */
export const CONTACT_LEVELS = ['Weekly', 'Bi-weekly', 'Monthly', 'As needed'] as const;

/** Housing condition options */
export const HOUSING_CONDITIONS = ['Stable', 'Needs Repair', 'Unsafe', 'Homeless'] as const;

/** Owner / renter status */
export const OWNER_RENTER_OPTIONS = ['Owner', 'Renter', 'Other'] as const;

/** Referral source options */
export const REFERRAL_SOURCE_OPTIONS = [
  'VSO Office',
  'VA Medical Center',
  'Community Partner',
  'Self-Referral',
  'Family Member',
  'Law Enforcement',
  'Other',
] as const;

/** Insurance coverage adequacy */
export const INSURANCE_ADEQUACY_OPTIONS = ['Sufficient', 'Under Insured', 'Unknown'] as const;

/** Insurance deductible ranges */
export const INSURANCE_DEDUCTIBLE_RANGES = ['$0–$499', '$500–$999', '$1,000–$1,999', '$2,000–$4,999', '$5,000+', 'Unknown'] as const;

/** Work order status domain */
export const WORK_ORDER_STATUSES = ['Unclaimed', 'Claimed', 'In Progress', 'Completed'] as const;
export type WorkOrderStatus = (typeof WORK_ORDER_STATUSES)[number];

/** Work order types — veterans service work orders */
export const WORK_ORDER_TYPES = [
  'Benefits Counseling',
  'Housing Assessment',
  'Employment Assistance',
  'Healthcare Navigation',
  'Legal Consultation',
  'Transportation Assistance',
] as const;

/**
 * Max document photos per case folder.
 */
export const DOCUMENT_PHOTO_MAX = 10;

/** Follow-up types */
export const FOLLOW_UP_TYPES = ['Phone', 'Email', 'In-person', 'Partner coordination'] as const;

/** Case note types */
export const NOTE_TYPES = [
  'Case note',
  'Client contact',
  'Partner coordination',
  'Home visit',
] as const;

/** Case closure reasons */
export const CLOSURE_REASONS = [
  'All needs met',
  'Client declined services',
  'Unable to reach client',
  'Duplicate record',
  'Transferred',
] as const;

/** Document types for upload classification */
export const DOCUMENT_TYPES = [
  'Release of Information',
  'VA Form 21-0781 (PTSD)',
  'DD-214 (Discharge Papers)',
  'Benefits Award Letter',
  'Service Treatment Records',
  'Disability Rating Documentation',
  'VA Healthcare Enrollment',
  'GI Bill Certificate of Eligibility',
  'Vocational Rehab Plan',
  'Housing Application',
  'Lease or Rental Agreement',
  'Proof of Income',
  'Proof of Identification',
  'Court Orders',
  'Legal Correspondence',
  'Financial Assistance Documentation',
  'Mental Health Treatment Records',
  'Caregiver Certification',
  'Emergency Contact Authorization',
  'Case Closure Summary',
  'Recovery Plan Documentation',
  'Other Agency Correspondence',
  'Confidential Documents',
] as const;

/** Closure checklist items */
export const CLOSURE_CHECKLIST_ITEMS = [
  'All service goals reviewed',
  'Referral outcomes recorded',
  'Final client follow-up completed',
  'Required closure documents uploaded',
  'Client satisfaction survey offered',
  'Case supervisor review completed',
] as const;

/** Co-applicant relationship options */
export const CO_APPLICANT_RELATIONSHIPS = [
  'Spouse / Partner',
  'Parent',
  'Child',
  'Sibling',
  'Roommate',
  'Caregiver',
  'Other Relative',
  'Other Non-relative',
] as const;

/** Household member relationship options */
export const HOUSEHOLD_RELATIONSHIPS = [
  'Self',
  'Spouse / Partner',
  'Child',
  'Parent',
  'Sibling',
  'Caregiver',
  'Adult - Relative',
  'Minor - Relative',
  'Adult - Non-relative',
  'Minor - Non-relative',
] as const;

/** Household member condition options */
export const HOUSEHOLD_CONDITIONS = [
  'Not Affected',
  'Injured',
  'Hospitalized',
  'Missing',
  'Deceased',
] as const;

/** Race options */
export const RACE_OPTIONS = [
  'American Indian or Alaska Native',
  'Asian',
  'Black or African American',
  'Native Hawaiian or Other Pacific Islander',
  'White',
] as const;

/** Ethnicity options */
export const ETHNICITY_OPTIONS = ['Hispanic or Latino', 'Not Hispanic or Latino'] as const;

/** Shelter service need options */
export const SHELTER_SERVICE_NEEDS = [
  'Health Services',
  'Mental Health Services',
  'Emotional and Spiritual Care',
  'Disability Accommodation',
  'Family Reunification',
  'Child Care',
] as const;

/** Goal status options */
export const GOAL_STATUSES = [
  'In Progress',
  'Goal Met',
  'Goal Partially Met',
  'Goal Unmet',
  'Goal Abandoned',
  'No Resources to Meet Goal',
] as const;

/** Service plan timeline options */
export const SERVICE_PLAN_TIMELINE_OPTIONS = [
  '1 month (30 days)',
  '2 months (60 days)',
  '3 months (90 days)',
  '4 months (120 days)',
  '5 months (150 days)',
  '6 months (180 days)',
  '7 months (210 days)',
  '8 months (240 days)',
  '9 months (270 days)',
  '10 months (300 days)',
  '11 months (330 days)',
  '12 months (365 days)',
] as const;

/** Referral lifecycle statuses */
export const REFERRAL_STATUSES = [
  'Requested',
  'Referred',
  'Accepted',
  'Provided',
  'Declined',
  'Unreached',
] as const;

/** Insurance coverage types */
export const INSURANCE_TYPES = ['VA Healthcare', 'TRICARE', 'Private Insurance', 'Medicaid', 'Medicare', 'None'] as const;

/** Insurance claim statuses */
export const CLAIM_STATUSES = ['Submitted', 'Approved', 'Denied', 'Pending'] as const;

/** VA claim application statuses */
export const VA_CLAIM_STATUSES = ['Not filed', 'Filed', 'In Review', 'Approved', 'Denied', 'Appealed'] as const;

/** Education benefit statuses */
export const EDUCATION_BENEFIT_STATUSES = ['Not applied', 'Applied', 'Approved', 'Denied'] as const;

/** Partner referral case statuses */
export const PARTNER_REFERRAL_STATUSES = ['Not referred', 'Pending', 'Active', 'Closed'] as const;

/** Appeal statuses */
export const APPEAL_STATUSES = [
  'Not needed',
  'Planned',
  'Submitted',
  'Approved',
  'Denied',
] as const;

/** Shelter statuses */
export const SHELTER_STATUSES = ['Not needed', 'Requested', 'Active', 'Exited'] as const;

/** Temporary housing statuses */
export const TEMP_HOUSING_STATUSES = [
  'Staying with friends or family',
  'Hotel',
  'Other',
] as const;

/** Permanent residence statuses */
export const PERMANENT_RESIDENCE_STATUSES = [
  'Repair in progress',
  'Rebuild in progress',
  'Relocation planned',
  'Resolved',
] as const;

/** Resource Library categories — veterans service categories */
export const RESOURCE_CATEGORIES = [
  'Benefits Navigation',
  'Compensation & Pension',
  'Education Benefits (GI Bill)',
  'Employment & Vocational Rehab',
  'Financial Assistance',
  'Food & Nutrition',
  'Healthcare Navigation',
  'Homeless Prevention',
  'Housing – Permanent',
  'Housing – Temporary',
  'Legal Assistance',
  'Mental Health Services',
  'MST Support',
  'Peer Support',
  'Physical Rehabilitation',
  'Substance Use Recovery',
  'Transportation',
  'Women Veterans Services',
  'Caregiver Support',
  'Other',
] as const;

/** Texas counties most relevant to the seeded program events. */
export const TX_COUNTIES = [
  "Anderson", "Andrews", "Angelina", "Aransas", "Archer", "Armstrong", "Atascosa", "Austin", "Bailey", "Bandera", "Bastrop", "Baylor", "Bee", "Bell", "Bexar", "Blanco", "Borden", "Bosque", "Bowie", "Brazoria", "Brazos", "Brewster", "Briscoe", "Brooks", "Brown", "Burleson", "Burnet", "Caldwell", "Calhoun", "Callahan", "Cameron", "Camp", "Carson", "Cass", "Castro", "Chambers", "Cherokee", "Childress", "Clay", "Cochran", "Coke", "Coleman", "Collin", "Collingsworth", "Colorado", "Comal", "Comanche", "Concho", "Cooke", "Coryell", "Cottle", "Crane", "Crockett", "Crosby", "Culberson", "Dallam", "Dallas", "Dawson", "Deaf Smith", "Delta", "Denton", "DeWitt", "Dickens", "Dimmit", "Donley", "Duval", "Eastland", "Ector", "Edwards", "Ellis", "El Paso", "Erath", "Falls", "Fannin", "Fayette", "Fisher", "Floyd", "Foard", "Fort Bend", "Franklin", "Freestone", "Frio", "Gaines", "Galveston", "Garza", "Gillespie", "Glasscock", "Goliad", "Gonzales", "Gray", "Grayson", "Gregg", "Grimes", "Guadalupe", "Hale", "Hall", "Hamilton", "Hansford", "Hardeman", "Hardin", "Harris", "Harrison", "Hartley", "Haskell", "Hays", "Hemphill", "Henderson", "Hidalgo", "Hill", "Hockley", "Hood", "Hopkins", "Houston", "Howard", "Hudspeth", "Hunt", "Hutchinson", "Irion", "Jack", "Jackson", "Jasper", "Jeff Davis", "Jefferson", "Jim Hogg", "Jim Wells", "Johnson", "Jones", "Karnes", "Kaufman", "Kendall", "Kenedy", "Kent", "Kerr", "Kimble", "King", "Kinney", "Kleberg", "Knox", "Lamar", "Lamb", "Lampasas", "La Salle", "Lavaca", "Lee", "Leon", "Liberty", "Limestone", "Lipscomb", "Live Oak", "Llano", "Loving", "Lubbock", "Lynn", "McCulloch", "McLennan", "McMullen", "Madison", "Marion", "Martin", "Mason", "Matagorda", "Maverick", "Medina", "Menard", "Midland", "Milam", "Mills", "Mitchell", "Montague", "Montgomery", "Moore", "Morris", "Motley", "Nacogdoches", "Navarro", "Newton", "Nolan", "Nueces", "Ochiltree", "Oldham", "Orange", "Palo Pinto", "Panola", "Parker", "Parmer", "Pecos", "Polk", "Potter", "Presidio", "Rains", "Randall", "Reagan", "Real", "Red River", "Reeves", "Refugio", "Roberts", "Robertson", "Rockwall", "Runnels", "Rusk", "Sabine", "San Augustine", "San Jacinto", "San Patricio", "San Saba", "Schleicher", "Scurry", "Shackelford", "Shelby", "Sherman", "Smith", "Somervell", "Starr", "Stephens", "Sterling", "Stonewall", "Sutton", "Swisher", "Tarrant", "Taylor", "Terrell", "Terry", "Throckmorton", "Titus", "Tom Green", "Travis", "Trinity", "Tyler", "Upshur", "Upton", "Uvalde", "Val Verde", "Van Zandt", "Victoria", "Walker", "Waller", "Ward", "Washington", "Webb", "Wharton", "Wheeler", "Wichita", "Wilbarger", "Willacy", "Williamson", "Wilson", "Winkler", "Wise", "Wood", "Yoakum", "Young", "Zapata", "Zavala",
] as const;

/** Resource record statuses — used in the Resource Library / Manage views. */
export const RESOURCE_STATUSES = ['Active', 'Inactive', 'Needs Review'] as const;

/** Recurring follow-up cadence options (Notes & Follow-Up tab). */
export const RECURRENCE_OPTIONS = [
  { label: 'Weekly', days: 7 },
  { label: 'Bi-weekly', days: 14 },
  { label: 'Every 3 weeks', days: 21 },
  { label: 'Monthly', days: 30 },
] as const;

/** Case record tabs */
export const CASE_TABS = [
  { id: 'summary', label: 'Summary' },
  { id: 'profile', label: 'Case Profile' },
  { id: 'intake', label: 'Client Intake' },
  { id: 'household', label: 'Household' },
  { id: 'assessment', label: 'Assessment' },
  { id: 'housing', label: 'Housing' },
  { id: 'insurance', label: 'Insurance' },
  { id: 'assistance', label: 'Assistance' },
  { id: 'service-plan', label: 'Service Plan' },
  { id: 'referrals', label: 'Referrals' },
  { id: 'notes', label: 'Notes & Follow-Up' },
  { id: 'documents', label: 'Documents' },
  { id: 'benefits', label: 'Benefits' },
  { id: 'audit', label: 'Audit Log' },
  { id: 'closure', label: 'Closure' },
] as const;
export type CaseTabId = (typeof CASE_TABS)[number]['id'];

/** Call Center — reason for call options. */
export const CALL_REASONS = [
  'Benefits inquiry (VA, SSVF, VHAP)',
  'Housing assistance request',
  'Employment / vocational rehab',
  'Mental health / counseling referral',
  'Substance use support',
  'Healthcare enrollment (VA / TRICARE)',
  'DD-214 / discharge documentation',
  'Disability claim assistance',
  'Financial assistance request',
  'Legal services referral',
  'Food / clothing / emergency supplies',
  'Transportation assistance',
  'Family / caregiver support',
  'Follow-up on previous call',
  'Other',
] as const;

/** Call Center — call outcome options. */
export const CALL_OUTCOMES = [
  'Contact Only',
  'Registered',
  'Referred',
  'Converted to Case',
] as const;

/** Military branch options */
export const MILITARY_BRANCHES = [
  'Army',
  'Navy',
  'Marine Corps',
  'Air Force',
  'Space Force',
  'Coast Guard',
  'National Guard',
  'Army Reserve',
  'Navy Reserve',
  'Marine Corps Reserve',
  'Air Force Reserve',
  'Coast Guard Reserve',
] as const;

/** Era of service options */
export const ERAS_OF_SERVICE = [
  'WWII',
  'Korean War',
  'Vietnam Era',
  'Gulf War (1990-1991)',
  'Post-9/11 (OEF/OIF/OND)',
  'Peacetime',
  'Other',
] as const;

/** Discharge status options */
export const DISCHARGE_STATUSES = [
  'Honorable',
  'General (Under Honorable Conditions)',
  'Other Than Honorable (OTH)',
  'Bad Conduct',
  'Dishonorable',
  'Unknown',
] as const;

/** Prior living situation options */
export const PRIOR_LIVING_OPTIONS = [
  'Owned / co-owned residence',
  'Rented',
  'Family or friends',
  'Public housing',
  'Mobile home',
  'Long-term care residence',
  'Hotel / motel',
  'Unsheltered / homeless',
  'Other',
] as const;

// ---------------------------------------------------------------------------
// Backward-compatibility aliases — existing TSX/page files import these names.
// They resolve to the renamed constants so no component changes are needed.
// ---------------------------------------------------------------------------

/** @deprecated Use VA_CLAIM_STATUSES */
export const FEMA_STATUSES = VA_CLAIM_STATUSES;

/** @deprecated Use EDUCATION_BENEFIT_STATUSES */
export const SBA_STATUSES = EDUCATION_BENEFIT_STATUSES;

/** @deprecated Use PARTNER_REFERRAL_STATUSES */
export const LTRG_STATUSES = PARTNER_REFERRAL_STATUSES;

/** @deprecated Use HOUSING_CONDITIONS */
export const DAMAGE_LEVELS = HOUSING_CONDITIONS;

/** @deprecated Disaster-specific — no longer applicable */
export const WATER_DEPTH_OPTIONS = ['None', 'Less than 1 ft', '1–3 ft', '3–6 ft', '6–10 ft', 'Over 10 ft'] as const;

/** @deprecated Disaster-specific — no longer applicable */
export const UTILITY_TYPES = ['Electric', 'Gas', 'Water', 'Sewer', 'Internet / Phone'] as const;

/** @deprecated Disaster-specific — no longer applicable */
export const CLEANUP_NEED_TYPES = ['Debris Removal', 'Muck & Gut', 'Mold Remediation', 'Hazardous Material', 'Tree Removal'] as const;

/** @deprecated Use REFERRAL_SOURCE_OPTIONS */
export const REPORTED_AGENCY_OPTIONS = REFERRAL_SOURCE_OPTIONS;

/** @deprecated Use DOCUMENT_PHOTO_MAX */
export const ISTAT_PHOTO_MAX = DOCUMENT_PHOTO_MAX;

/** @deprecated Use SERVICE_PLAN_TIMELINE_OPTIONS */
export const RECOVERY_TIMELINE_OPTIONS = SERVICE_PLAN_TIMELINE_OPTIONS;
